import { MeetingType } from 'api/generated/types/meetingType';
import { getMeetingTypeOptions } from 'constants/meetingTypeLabels';
import { parseEnum } from 'utils/typeGuards';

import { useIsStaffing } from './useIsStaffing';
import { useStaffingDiscoveryMeetingTypeEnabled } from './useStaffingDiscoveryMeetingTypeEnabled';

export type MeetingTypeFlagGates = {
  /** Customer-facing staffing types (Staffing Sales). */
  isStaffing: boolean;
  /** Internal Staffing Sales Discovery type. */
  staffingDiscoveryEnabled: boolean;
};

/** Meeting types gated for staffing-company customers. */
export const STAFFING_CUSTOMER_MEETING_TYPES: ReadonlySet<MeetingType> =
  new Set(['sales']);

/** Meeting types gated by the internal discovery flag. */
export const STAFFING_DISCOVERY_MEETING_TYPES: ReadonlySet<MeetingType> =
  new Set(['staffing_discovery']);

// Pure helper so unit tests don't need the feature-flag store.
export function filterMeetingTypeOptions(
  options: { value: MeetingType; label: string }[],
  gates: MeetingTypeFlagGates,
  currentValue?: string | null
): { value: MeetingType; label: string }[] {
  // Keep the currently selected value visible so existing notes/presets remain
  // editable when their gate is off.
  return options.filter((opt) => {
    if (opt.value === currentValue) return true;
    if (STAFFING_CUSTOMER_MEETING_TYPES.has(opt.value)) return gates.isStaffing;
    if (STAFFING_DISCOVERY_MEETING_TYPES.has(opt.value)) {
      return gates.staffingDiscoveryEnabled;
    }
    return true;
  });
}

/** True when a preset/note type should be hidden for the current org gates. */
export function isMeetingTypeHiddenByFlags(
  type: string | null | undefined,
  gates: MeetingTypeFlagGates
): boolean {
  const value = parseEnum(MeetingType, type, MeetingType.other);
  if (STAFFING_CUSTOMER_MEETING_TYPES.has(value)) return !gates.isStaffing;
  if (STAFFING_DISCOVERY_MEETING_TYPES.has(value)) {
    return !gates.staffingDiscoveryEnabled;
  }
  return false;
}

// Gates staffing-related meeting types behind their respective flags. Pass the
// currently selected value so an existing staffing note/preset keeps its option
// visible even when the flag is off.
export function useMeetingTypeOptions(
  currentValue?: string | null
): { value: MeetingType; label: string }[] {
  const isStaffing = useIsStaffing();
  const staffingDiscoveryEnabled = useStaffingDiscoveryMeetingTypeEnabled();
  return filterMeetingTypeOptions(
    getMeetingTypeOptions(),
    { isStaffing, staffingDiscoveryEnabled },
    currentValue
  );
}
